import { InputConfig } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationJobPositionContractTypeChoices,
  AuthAccountOrganizationJobPositionDaysOffChoices,
  AuthAccountOrganizationJobPositionPaymentTermChoices,
  useGetJobBenefitsLazyQuery,
} from "@graphql/generated";

const hoursOptions = [
  { label: "6 AM", value: "06:00:00" },
  { label: "7 AM", value: "07:00:00" },
  { label: "8 AM", value: "08:00:00" },
  { label: "9 AM", value: "09:00:00" },
  { label: "10 AM", value: "10:00:00" },
  { label: "11 AM", value: "11:00:00" },
  { label: "12 PM", value: "12:00:00" },
  { label: "1 PM", value: "13:00:00" },
  { label: "2 PM", value: "14:00:00" },
  { label: "3 PM", value: "15:00:00" },
  { label: "4 PM", value: "16:00:00" },
  { label: "5 PM", value: "17:00:00" },
  { label: "6 PM", value: "18:00:00" },
  { label: "7 PM", value: "19:00:00" },
  { label: "8 PM", value: "20:00:00" },
  { label: "9 PM", value: "21:00:00" },
  { label: "10 PM", value: "22:00:00" },
  { label: "11 PM", value: "23:00:00" },
];

const MIN_SALARY = 15;
const MAX_SALARY = 300;

export const getInputConfig = ({ formData }: { formData: any }): InputConfig[] => [
  {
    name: "contractType",
    previewSize: {
      sm: 12,
      md: 6,
      lg: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Contract Type:",
      placeholder: "Contract Type",
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Apprenticeship", value: AuthAccountOrganizationJobPositionContractTypeChoices.APPRENTICESHIP },
        { label: "Fix Term Contract", value: AuthAccountOrganizationJobPositionContractTypeChoices.FIX_TERM_CONTRACT },
        { label: "Freelance", value: AuthAccountOrganizationJobPositionContractTypeChoices.FREELANCE },
        { label: "Full Time", value: AuthAccountOrganizationJobPositionContractTypeChoices.FULL_TIME },
        { label: "Internship/Co-op", value: AuthAccountOrganizationJobPositionContractTypeChoices.INTERNSHIP_CO_OP },
        { label: "Part Time", value: AuthAccountOrganizationJobPositionContractTypeChoices.PART_TIME },
        { label: "Permanent", value: AuthAccountOrganizationJobPositionContractTypeChoices.PERMANENT },
        { label: "Seasonal", value: AuthAccountOrganizationJobPositionContractTypeChoices.SEASONAL },
      ],
    },
    rules: {
      required: "This field is required.",
    },
  },
  {
    name: "salaryMin",
    previewSize: {
      sm: 6,
      md: 6,
      lg: 2,
    },
    inputProps: {
      type: "number",
      label: "Min Hourly Salary:",
      placeholder: "Min",
    },
    rules: {
      required: "This field is required.",
      min: { value: MIN_SALARY, message: `Min salary must be at least ${MIN_SALARY}` },
      max: { value: MAX_SALARY, message: `Min salary must be at most ${MAX_SALARY}` },
      validate: (value: string) => {
        if (!value || value == "undefined") {
          return "This field is required.";
        }
        return true;
      },
    },
  },
  {
    name: "salaryMax",
    previewSize: {
      sm: 6,
      md: 6,
      lg: 2,
    },
    inputProps: {
      type: "number",
      label: "Max Hourly Salary:",
      placeholder: "Max",
    },
    rules: {
      required: "This field is required.",
      min: { value: MIN_SALARY, message: `Max salary must be at least ${MIN_SALARY}` },
      max: { value: MAX_SALARY, message: `Max salary must be at most ${MAX_SALARY}` },
      validate: (value: string) => {
        if (!value || value == "undefined") {
          return "This field is required.";
        }
        if (value <= formData.salaryMin) {
          return "Max salary must be greater than Min salary";
        }
        return true;
      },
    },
  },
  {
    name: "paymentTerm",
    previewSize: {
      sm: 12,
      md: 6,
      lg: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Payment Terms:",
      placeholder: "Payment Terms",
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Hourly", value: AuthAccountOrganizationJobPositionPaymentTermChoices.HOURLY },
        { label: "Daily", value: AuthAccountOrganizationJobPositionPaymentTermChoices.DAILY },
        { label: "Weekly", value: AuthAccountOrganizationJobPositionPaymentTermChoices.WEEKLY },
        { label: "Bi-Weekly", value: AuthAccountOrganizationJobPositionPaymentTermChoices.BI_WEEKLY },
        { label: "Monthly", value: AuthAccountOrganizationJobPositionPaymentTermChoices.MONTHLY },
        { label: "Yearly", value: AuthAccountOrganizationJobPositionPaymentTermChoices.YEARLY },
      ],
    },
    rules: {
      required: "This field is required.",
    },
  },
  {
    name: "workingStartAt",
    previewSize: {
      sm: 6,
      md: 6,
      lg: 2,
    },
    inputProps: {
      type: "searcher",
      label: "Working Start At:",
      placeholder: "Start",
      labelKey: "label",
      valueKey: "value",
      items: hoursOptions,
    },
  },
  {
    name: "workingEndAt",
    previewSize: {
      sm: 6,
      md: 6,
      lg: 2,
    },
    inputProps: {
      type: "searcher",
      label: "Working End At:",
      placeholder: "End",
      labelKey: "label",
      valueKey: "value",
      items: hoursOptions,
    },
    rules: {
      validate: (value: any) => {
        if (formData.workingStartAt && formData.workingStartAt.value) {
          const startAt = parseInt(formData.workingStartAt.value as string, 10);
          return parseInt(value.value, 10) >= startAt || "Working end at must be greater than working start at";
        }
        return true;
      },
    },
  },
  {
    name: "daysOff",
    previewSize: {
      sm: 12,
      md: 6,
      lg: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Days Off:",
      placeholder: "Days Off",
      isMulti: true,
      labelKey: "label",
      valueKey: "value",
      items: [
        { label: "Monday", value: AuthAccountOrganizationJobPositionDaysOffChoices.MONDAY },
        { label: "Tuesday", value: AuthAccountOrganizationJobPositionDaysOffChoices.TUESDAY },
        { label: "Wednesday", value: AuthAccountOrganizationJobPositionDaysOffChoices.WEDNESDAY },
        { label: "Thursday", value: AuthAccountOrganizationJobPositionDaysOffChoices.THURSDAY },
        { label: "Friday", value: AuthAccountOrganizationJobPositionDaysOffChoices.FRIDAY },
        { label: "Saturday", value: AuthAccountOrganizationJobPositionDaysOffChoices.SATURDAY },
        { label: "Sunday", value: AuthAccountOrganizationJobPositionDaysOffChoices.SUNDAY },
      ],
    },
  },
  {
    name: "benefits",
    previewSize: {
      sm: 12,
      md: 6,
      lg: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Benefits:",
      placeholder: "Benefits",
      isMulti: true,
      useLazyQuery: useGetJobBenefitsLazyQuery,
      dataPath: "common.jobBenefits",
      labelKey: "name",
    },
  },
  {
    name: "otherBenefits",
    previewSize: {
      sm: 12,
      md: 12,
      lg: 8,
    },
    inputProps: {
      type: "text",
      label: "Other Benefits:",
      placeholder: "Enter caption /descripton for benefits ",
    },
  },
];
